import React, { useEffect, useMemo, useState } from 'react';
import { filter, find, get, includes, isEmpty, omit, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Collapse, Grid2, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import useValidationSchema from './_formComment';
import { createComment, editComment } from './_api';

import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import FormDictionaryInput from 'components/Form/DictionaryInput/DictionaryInput';
import { Box } from '@mui/system';
import { IComment, ISelectItem, ISrMacroDictionary } from './_types';
import { useLanguage } from 'utils/hooks/useLanguage';
import { useUser } from 'utils/hooks/useUser';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { IPriorityConfig, IStatusConfig } from 'modules/Administration/Workplaces/_types';
import { FINISHED, SECOND_READ } from 'constants/constants';
import Button from 'components/Buttons/Button';
import { initNovaVoice } from './_novaVoice';
import { getStudyReportTemplates } from 'modules/Administration/GeneralSettings/_apiStudyReportTemplate';

const StudyDetailCommentForm: React.FC<any> = ({
  setDialogState,
  commentItem,
  getComments,
  study,
  initialComment,
  allStatuses,
  allPriorities,
}) => {
  const { t } = useTranslation('StudyDetail');
  const { hasRole, user } = useUser();
  const { priorityAndStatusEnabled, feConfig } = useAppInfo();
  const { toggleLoader } = useAppGlobals();
  const { currentLocale } = useLanguage();
  const [formError, setFormError] = useState<any>(null);
  const [priorityItems, setPriorityItems] = useState<ISelectItem[]>([]);
  const [isFinishedType, setIsFinishedType] = useState<boolean>(false);
  const [isSecondReadType, setIsSecondReadType] = useState<boolean>(false);

  const [studyReportTemplatesDictionary, setStudyReportTemplatesDictionary] = useState<
    ISrMacroDictionary[] | undefined
  >(undefined);

  const hasRoleStudyReportTemplateUsage = hasRole('ROLE_STUDY_REPORT_TEMPLATE_USAGE');

  const [isCreating, id, hasCreateSupplement, commentItemPrepared] = useMemo(() => {
    const id = get(commentItem, 'id');
    const hasCreateSupplement = get(commentItem, 'hasCreateSupplement', false);
    const isCreating = !id || hasCreateSupplement;
    const commentItemPrepared = isCreating ? null : { ...commentItem };
    return [isCreating, id, hasCreateSupplement, commentItemPrepared];
  }, [commentItem]);

  const [texts, setTexts] = useState<any[]>([isCreating ? '' : get(commentItem, 'text', '')]);

  const commentCreatedByLoggedUser = useMemo(() => {
    return get(commentItemPrepared, 'createdBy.username') === get(user, 'sub');
  }, [commentItemPrepared, user]);

  const { CommentFormSchema } = useValidationSchema(true);
  const methods = useForm<any>({
    resolver: yupResolver(CommentFormSchema),
  });
  const {
    handleSubmit,
    formState: { errors },
    register,
    reset,
    setValue,
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const prepareValues: IComment = {
      ...omit(values, ['id', 'text']),
      comment: { id: values.id || 0, text: values.text },
    };

    const fn = isCreating ? createComment : editComment;
    await fn(prepareValues).then(
      async () => {
        await getComments();
        setDialogState(false);
      },
      () => setFormError(t('Comment:errorSaving')),
    );
    toggleLoader(false);
  });

  const getEntities = async () => {
    toggleLoader();
    try {
      const defaultValues = isCreating
        ? {
            productId: parseInt(get(study, 'archive.id'), 10),
            studyUid: get(study, 'studyInstanceUid'),
            ...(hasCreateSupplement ? { supplementFor: id } : {}),
            ...(initialComment !== null
              ? {
                  comment: { text: initialComment },
                }
              : { comment: { text: '' } }),
          }
        : {
            id,
            text: commentItemPrepared.text,
          };
      if (priorityAndStatusEnabled) {
        let defaultPriority = '';
        let finishedType = '';
        let secondReadTypes: string[] = [];
        if (!isEmpty(allStatuses) && !isEmpty(allPriorities)) {
          const statuses: IStatusConfig[] = [...allStatuses];
          const priorites: IPriorityConfig[] = [...allPriorities];
          defaultPriority = get(find(allPriorities, { default: true }), 'code', '');
          finishedType = get(find(statuses, { type: FINISHED }), 'code', '');
          secondReadTypes = filter(statuses, { type: SECOND_READ }).map((item) => item.code);

          setPriorityItems(
            priorites.map((item) => ({
              id: item.code,
              label: get(item, `name.${currentLocale}`, get(item, 'name.cs', 'name')),
            })),
          );
        }

        const isFinishedType = get(commentItemPrepared, 'commentStatusCode') === finishedType;
        const isSecondReadType = includes(
          secondReadTypes,
          get(commentItemPrepared, 'commentStatusCode'),
        );
        setIsFinishedType(isFinishedType);
        setIsSecondReadType(isSecondReadType);
        reset({
          ...defaultValues,
          commentPriorityCode: get(commentItemPrepared, 'commentPriorityCode') || defaultPriority,
        });
      } else {
        reset({ ...defaultValues });
      }
      const studyReportTemplatesResponse = await getStudyReportTemplates();
      if (studyReportTemplatesResponse) {
        const sortedStudyReportTemplates = orderBy(
          studyReportTemplatesResponse,
          ['keyword'],
          ['desc'],
        );
        const studyReportTemplatesDictionary = sortedStudyReportTemplates.map((value, index) => {
          const keyword = get(value, 'keyword');
          const content = get(value, 'content', 'value');
          return {
            name: keyword || 'keyword',
            value: content || 'value',
            caption: keyword || 'caption',
            meta: content || 'content',
            score: index,
          };
        });
        setStudyReportTemplatesDictionary(studyReportTemplatesDictionary);
      }
    } catch (e) {
      console.debug(e);
    }

    toggleLoader(false);
  };

  useEffect(() => {
    getEntities();
    let myConn = null;
    if (get(feConfig, 'novaVoiceEnabled', false)) {
      console.log('Initializing NovaVoice connection');
      myConn = initNovaVoice();
    }
    return () => {
      if (myConn) {
        myConn.then((conn) => conn.close());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (texts !== undefined) {
      (texts || []).forEach((item) => {
        setValue('text', item, { shouldValidate: false });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texts]);

  return (
    <>
      {commentItemPrepared || isCreating ? (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            {isCreating ? (
              <>
                <input {...register('productId')} type="hidden" />
                <input {...register('studyUid')} type="hidden" />
                <input {...register('comment.text')} type="hidden" />
                <input {...register('supplementFor')} type="hidden" />
              </>
            ) : (
              <>
                <input {...register('id')} type="hidden" />
              </>
            )}
            <Grid2 container={true} spacing={0}>
              <Grid2 size={12}>
                {hasRoleStudyReportTemplateUsage ? (
                  studyReportTemplatesDictionary !== undefined && texts !== undefined ? (
                    <FormDictionaryInput
                      name={'text'}
                      items={studyReportTemplatesDictionary}
                      required={true}
                      autoFocus={true}
                      texts={texts}
                      setTexts={setTexts}
                      index={0}
                    />
                  ) : null
                ) : (
                  <FormInput
                    name={'text'}
                    label={t('text')}
                    required={true}
                    multiline={true}
                    rows={16}
                    autoFocus={true}
                    customOnChange={(e: any) => {
                      const text = e.target.value;
                      console.debug(text);
                    }}
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                <Collapse in={errors ? true : false} timeout="auto">
                  {errors && (
                    <Typography align="center" color="error">
                      {formError}
                    </Typography>
                  )}
                </Collapse>
              </Grid2>
              <Grid2 size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    displayPrint: 'none',
                  }}
                >
                  <Box>
                    {priorityAndStatusEnabled && (
                      <FormSelect
                        name="commentPriorityCode"
                        label={t('PriorityStatusesConfig:priority')}
                        items={priorityItems}
                        disabled={
                          !(
                            !isFinishedType &&
                            !isSecondReadType &&
                            (isCreating ||
                              (!isCreating &&
                                (commentCreatedByLoggedUser ||
                                  hasRole('ROLE_MANAGE_COMMENT_PRIORITIES'))))
                          )
                        }
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'inline-flex', mt: 1 }}>
                    <Button
                      onClick={() => setDialogState(false)}
                      variant="contained"
                      color="inherit"
                      sx={{ mr: 1 }}
                    >
                      {t('close')}
                    </Button>
                    <Button variant="contained" color="primary" type="submit">
                      <SaveIcon sx={{ mr: 1 }} />
                      {t('confirm')}
                    </Button>
                  </Box>
                </Box>
              </Grid2>
            </Grid2>
          </form>
        </FormProvider>
      ) : (
        <Papeer>
          <Typography>{t('commentNotFound')}</Typography>
        </Papeer>
      )}
    </>
  );
};

export default StudyDetailCommentForm;
